/**
 * Header — componente customizado do odc
 *
 * Semantic UI-free: usa @plone/components (React Aria) para Container.
 * Layout: botão "Menu" (hambúrguer + rótulo) à esquerda, marcas da Década do
 * Oceano e da Conferência centralizadas e seletor de idioma (globo + código)
 * à direita — ajuste pedido pela UNESCO (2026-09), ver
 * docs/specs/header-nav.md. No mobile o rótulo "Menu" e o idioma saem do
 * header (o idioma vai para o drawer) para as duas marcas caberem numa linha.
 *
 * Padrão baseado no volto-light-theme:
 *   customizations/volto/.../Header.jsx → re-exporta deste arquivo
 *
 * O hambúrguer abre o menu lateral (HeaderNavDrawer), desenhado em
 * docs/layout/Complemento.png — ver docs/specs/header-nav.md.
 */
import { useEffect, useRef, useState } from 'react';
import cx from 'clsx';
import { defineMessages, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Container } from '@plone/components';

import Icon from '@plone/volto/components/theme/Icon/Icon';
import menuSVG from '../../icons/menu.svg';
import logoOceanDecade from '../../assets/logos/logo-ocean-decade.svg';
import logoOdcConference from '../../assets/logos/logo-odc-conference.svg';
import HeaderLanguageSelector from './HeaderLanguageSelector';
import HeaderNavDrawer from './HeaderNavDrawer';

const messages = defineMessages({
  home: {
    id: 'Home',
    defaultMessage: 'Home',
  },
  logoAlt: {
    id: 'header.logoAlt',
    defaultMessage: 'Ocean Decade Conference 2027',
  },
  oceanDecadeLogoAlt: {
    id: 'header.oceanDecadeLogoAlt',
    defaultMessage:
      'United Nations Decade of Ocean Science for Sustainable Development 2021-2030',
  },
  openMenu: {
    id: 'header.openMenu',
    defaultMessage: 'Open menu',
  },
  menu: {
    id: 'header.menu',
    defaultMessage: 'Menu',
  },
});

type HeaderProps = {
  pathname: string;
};

/** Scroll a partir do qual o header fixa no topo, em versão compacta. */
const COMPACT_AFTER = 80;

const Header = ({ pathname }: HeaderProps) => {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  // Altura do header no estado normal — vira a altura do espaçador quando ele
  // sai do fluxo (position: fixed). Ver Header.scss.
  const [naturalHeight, setNaturalHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);

  // Navegar fecha o menu (o drawer também fecha no clique do próprio link,
  // mas isto cobre "voltar" do navegador e links de dentro do conteúdo).
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // A home é uma landing de página única, então o header acompanha a leitura
  // inteira: passa a `fixed` e encolhe depois dos primeiros pixels de scroll.
  useEffect(() => {
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        setCompact(window.scrollY > COMPACT_AFTER);
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  // Mede a altura natural só enquanto o header está no fluxo (medir no estado
  // compacto daria a altura errada). ResizeObserver em vez de medir uma vez:
  // na montagem o logo (SVG) ainda não tem layout e a medida sai 56px menor,
  // o que fazia o conteúdo pular quando o header virava fixed.
  useEffect(() => {
    if (compact) return undefined;
    const el = headerRef.current;
    if (!el) return undefined;

    const measure = () => setNaturalHeight(el.offsetHeight);
    measure();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [compact]);

  return (
    <>
      {compact && naturalHeight > 0 && (
        <div
          className="header-spacer"
          style={{ height: naturalHeight }}
          aria-hidden="true"
        />
      )}
      <header
        ref={headerRef}
        className={cx('header-wrapper', { 'header-wrapper--compact': compact })}
        role="banner"
      >
        <Container>
          <div className="header">
            <button
              type="button"
              className="header__menu-toggle"
              aria-label={intl.formatMessage(messages.openMenu)}
              aria-expanded={menuOpen}
              aria-controls="header-nav-drawer"
              onClick={() => setMenuOpen(true)}
            >
              <Icon name={menuSVG} size="20px" />
              <span className="header__menu-label">
                {intl.formatMessage(messages.menu)}
              </span>
            </button>

            {/* Duas marcas lado a lado, o PAR centralizado na página (pedido
                do cliente, 2026-09-24; medido em Proposta Layout 01-web.png).
                Só a marca da Conferência leva ao início — a da Década é
                institucional e não é link. */}
            <div className="header__brands">
              <img
                className="header__brand header__brand--decade"
                src={logoOceanDecade}
                alt={intl.formatMessage(messages.oceanDecadeLogoAlt)}
              />
              <Link
                to="/"
                className="header__logo"
                aria-label={intl.formatMessage(messages.home)}
              >
                <img
                  className="header__brand header__brand--odc"
                  src={logoOdcConference}
                  alt={intl.formatMessage(messages.logoAlt)}
                />
              </Link>
            </div>

            <div className="header__nav-tools">
              <HeaderLanguageSelector />
            </div>
          </div>
        </Container>

        <HeaderNavDrawer open={menuOpen} onClose={() => setMenuOpen(false)} />
      </header>
    </>
  );
};

export default Header;
